import { EventType } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { CreatePromptInput, PromptLabel, PromptListRecord, UpdatePromptInput } from '@/domain/prompt.types'
import { isRunSettled } from '@/domain/prompt.types'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { promptsApi } from '@/services/prompt.service'

const ROOT = 'prompts'

/** The async results this feature waits on, by the events that announce them. */
const EVAL_EVENTS = [EventType.PROMPT_EVAL_COMPLETED, EventType.PROMPT_EVAL_FAILED]
const DRAFT_EVENTS = [EventType.PROMPT_TEST_CASES_GENERATED, EventType.PROMPT_OUTPUT_SCHEMA_GENERATED]

/** Every key is rooted under `ROOT`, so invalidation stays inside this feature. */
export const promptKeys = {
	all: [ROOT] as const,
	list: () => [ROOT, 'list'] as const,
	detail: (id: string | null) => [ROOT, 'detail', id] as const,
	versions: (id: string | null) => [ROOT, 'versions', id] as const,
	labels: (id: string | null) => [ROOT, 'labels', id] as const,
	testCases: (id: string | null) => [ROOT, 'test-cases', id] as const,
	evalRuns: (id: string | null) => [ROOT, 'eval-runs', id] as const,
	evalGate: (id: string | null) => [ROOT, 'eval-gate', id] as const,
	applicable: (contextKey: string) => [ROOT, 'applicable', contextKey] as const,
	evalRun: (runId: string | null) => [ROOT, 'eval-run', runId] as const,
	lists: () => [ROOT, 'lists'] as const,
	listReferences: (id: string | null) => [ROOT, 'list-references', id] as const,
	defaults: () => [ROOT, 'defaults'] as const,
}

/** How often an in-flight eval run is re-read while waiting for it to settle. */
const RUN_POLL_MS = 4000

export const usePrompts = () => useQuery({ queryKey: promptKeys.list(), queryFn: promptsApi.list })

export const usePrompt = (id: string | null) =>
	useQuery({
		queryKey: promptKeys.detail(id),
		queryFn: () => promptsApi.byId(id as string),
		enabled: !!id,
	})

export const usePromptVersions = (id: string | null) =>
	useQuery({
		queryKey: promptKeys.versions(id),
		queryFn: () => promptsApi.versions(id as string),
		enabled: !!id,
	})

export const usePromptLabels = (id: string | null) =>
	useQuery({
		queryKey: promptKeys.labels(id),
		queryFn: () => promptsApi.labels(id as string),
		enabled: !!id,
	})

export const usePromptTestCases = (id: string | null) =>
	useQuery({
		queryKey: promptKeys.testCases(id),
		queryFn: () => promptsApi.testCases(id as string),
		enabled: !!id,
	})

export const usePromptEvalRuns = (id: string | null) =>
	useQuery({
		queryKey: promptKeys.evalRuns(id),
		queryFn: () => promptsApi.evalRuns(id as string),
		enabled: !!id,
	})

/**
 * One eval run, polled until it settles.
 *
 * Polling is the reconcile path, not the primary one — the realtime channel is —
 * but a client that missed a notification (reloaded mid-run, lost the socket) must
 * still converge. `refetchInterval` returns false once settled so a finished run
 * stops costing requests.
 */
export const useEvalRun = (runId: string | null, promptId?: string | null) => {
	const qc = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	const query = useQuery({
		queryKey: promptKeys.evalRun(runId),
		queryFn: () => promptsApi.evalRun(runId as string),
		enabled: !!runId,
		refetchInterval: (query) => {
			// The subscription does the waiting when the socket is up; polling stays as the
			// reconcile path for a client that reloaded mid-run or lost the socket.
			if (isPushing) return false
			const status = query.state.data?.status
			return status !== undefined && isRunSettled(status) ? false : RUN_POLL_MS
		},
	})

	const status = query.data?.status
	const settled = status !== undefined && isRunSettled(status)

	useEffect(() => {
		if (!isPushing || !runId || !promptId || settled) return

		// Subscribed on the PROMPT, not the run: the events are emitted with a `prompt`
		// entityRef (that is what a user is permissioned on), so the run id is not
		// addressable as a topic.
		return subscribeRealtime({ type: EVAL_EVENTS, entityRef: { id: [promptId] } }, () => {
			// Invalidate the run AND its history: the event says the run ended, not what it
			// produced, and the list view shows the same settled counts.
			void qc.invalidateQueries({ queryKey: promptKeys.evalRun(runId) })
			void qc.invalidateQueries({ queryKey: promptKeys.evalRuns(promptId) })
			void qc.invalidateQueries({ queryKey: promptKeys.evalGate(promptId) })
		})
	}, [isPushing, runId, promptId, settled, qc])

	return query
}

/**
 * Push-driven refresh for the async results that land on a prompt's own tabs:
 * generated test cases and a drafted output schema.
 *
 * Separate from `useEvalRun` because these have no run row to poll — before this
 * they only appeared on a manual reload.
 */
export const usePromptAsyncResults = (promptId: string | null) => {
	const qc = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	useEffect(() => {
		if (!isPushing || !promptId) return

		return subscribeRealtime({ type: DRAFT_EVENTS, entityRef: { id: [promptId] } }, () => {
			void qc.invalidateQueries({ queryKey: promptKeys.testCases(promptId) })
			void qc.invalidateQueries({ queryKey: promptKeys.detail(promptId) })
		})
	}, [isPushing, promptId, qc])
}

/**
 * Prompts narrowed by a classification tuple.
 *
 * Enabled only once at least one facet is selected: with an empty tuple every prompt
 * applies, and the plain list read already answers that — spending a resolution
 * query to get the same rows back would be waste.
 */
export const useApplicablePrompts = (context: Record<string, string[]>, enabled: boolean) => {
	// Serialised so the key is stable: the caller rebuilds the tuple object on every
	// render, and an object key would refetch each time.
	const contextKey = JSON.stringify(context)
	return useQuery({
		queryKey: promptKeys.applicable(contextKey),
		queryFn: () => promptsApi.applicable(context),
		enabled,
	})
}

export const usePromptLists = () => useQuery({ queryKey: promptKeys.lists(), queryFn: promptsApi.lists })

export const usePromptListReferences = (id: string | null) =>
	useQuery({
		queryKey: promptKeys.listReferences(id),
		queryFn: () => promptsApi.listReferences(id as string),
		enabled: !!id,
	})

export const usePromptDefaults = () => useQuery({ queryKey: promptKeys.defaults(), queryFn: promptsApi.defaults })

// ─── Mutations ────────────────────────────────────────────────────────────────

export const useCreatePrompt = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (input: CreatePromptInput) => promptsApi.create(input),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: promptKeys.list() })
		},
	})
}

export const useUpdatePrompt = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, patch }: { id: string; patch: UpdatePromptInput }) => promptsApi.update(id, patch),
		onSuccess: (_data, { id }) => {
			void qc.invalidateQueries({ queryKey: promptKeys.list() })
			void qc.invalidateQueries({ queryKey: promptKeys.detail(id) })
		},
	})
}

/**
 * Publishing changes the head's lifecycle AND mints a version AND may move labels,
 * so all three read paths are invalidated rather than just the one that was called.
 */
/**
 * The eval gate for the publish dialog.
 *
 * Enabled only while the dialog is open: it is a pre-publish check, and fetching it
 * for every prompt detail view would spend a request per page load on a question
 * nobody asked yet.
 */
export const usePromptEvalGate = (id: string | null, enabled: boolean) =>
	useQuery({
		queryKey: promptKeys.evalGate(id),
		queryFn: () => promptsApi.evalGate(id as string),
		enabled: Boolean(id) && enabled,
		// Always refetched when the dialog opens, never served from cache.
		//
		// The client's 5-minute `staleTime` meant a second open within that window replayed the
		// previous verdict — and because `isPending` is false when cached data exists, it did so
		// with no loading state, asserting "the last evaluation passed" for a body edited since.
		// The gate is invalidated on publish and on a run settling, but neither covers "the
		// author changed the prompt and reopened". The server re-checks before publishing, so
		// this misinformed rather than mispublished; still, this dialog's entire job is to be
		// the evidence a human decides on.
		staleTime: 0,
		refetchOnMount: 'always',
	})

export const usePublishPrompt = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => promptsApi.publish(id),
		onSuccess: (_data, id) => {
			void qc.invalidateQueries({ queryKey: promptKeys.detail(id) })
			void qc.invalidateQueries({ queryKey: promptKeys.versions(id) })
			void qc.invalidateQueries({ queryKey: promptKeys.labels(id) })
			void qc.invalidateQueries({ queryKey: promptKeys.list() })
			void qc.invalidateQueries({ queryKey: promptKeys.evalGate(id) })
		},
	})
}

export const useMoveLabel = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			label,
			version,
			pinned,
		}: {
			id: string
			label: PromptLabel
			version: number
			pinned?: boolean
		}) => promptsApi.moveLabel(id, label, version, pinned),
		onSuccess: (_data, { id }) => {
			void qc.invalidateQueries({ queryKey: promptKeys.labels(id) })
		},
	})
}

export const useCreateTestCase = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			input,
		}: {
			id: string
			input: { name: string; values: Record<string, unknown>; assertions: unknown[] }
		}) => promptsApi.createTestCase(id, input as never),
		onSuccess: (_data, { id }) => {
			void qc.invalidateQueries({ queryKey: promptKeys.testCases(id) })
		},
	})
}

export const useDeleteTestCase = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ caseId }: { promptId: string; caseId: string }) => promptsApi.deleteTestCase(caseId),
		onSuccess: (_data, { promptId }) => {
			void qc.invalidateQueries({ queryKey: promptKeys.testCases(promptId) })
		},
	})
}

/**
 * Trigger an eval run. Resolves with the run id as soon as the work is ACCEPTED —
 * the caller then watches the run rather than awaiting a result here.
 */
export const useRequestEvalRun = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => promptsApi.requestEvalRun(id),
		onSuccess: (_data, id) => {
			void qc.invalidateQueries({ queryKey: promptKeys.evalRuns(id) })
		},
	})
}

/** Drafting is asynchronous: this resolves on acceptance, not on arrival. */
export const useGenerateTestCases = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => promptsApi.generateTestCases(id),
		onSuccess: (_data, id) => {
			void qc.invalidateQueries({ queryKey: promptKeys.testCases(id) })
		},
	})
}

export const useClassifyPrompt = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, classification }: { id: string; classification: Record<string, string[]> }) =>
			promptsApi.classify(id, classification),
		onSuccess: (_data, { id }) => {
			void qc.invalidateQueries({ queryKey: promptKeys.detail(id) })
		},
	})
}

export const useGenerateOutputSchema = () =>
	useMutation({ mutationFn: (id: string) => promptsApi.generateOutputSchema(id) })

export const useCreatePromptList = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (input: { code: string; name: string; items: { value: string; label: string }[] }) =>
			promptsApi.createList(input),
		// A create has nothing to patch optimistically — the server assigns the id.
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: promptKeys.lists() })
		},
	})
}

export const useUpdatePromptList = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, patch }: { id: string; patch: Partial<PromptListRecord> }) => promptsApi.updateList(id, patch),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: promptKeys.lists() })
		},
	})
}

/**
 * Soft-delete a prompt.
 *
 * The server refuses one whose `code` live code resolves by, so the failure a caller
 * must surface is a CONFLICT with a reason worth reading — "archive it instead" —
 * rather than a generic error. Not optimistic: removing the row from the list before
 * the server agrees would make a refused delete look like it succeeded and then
 * reappear.
 */
export const useDeletePrompt = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => promptsApi.remove(id),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: promptKeys.all })
		},
	})
}

export const useDeletePromptList = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => promptsApi.deleteList(id),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: promptKeys.lists() })
		},
	})
}

export const useUpdatePromptDefaults = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ promptType, modelConfig }: { promptType: string; modelConfig: Record<string, unknown> }) =>
			promptsApi.updateDefaults(promptType, modelConfig),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: promptKeys.defaults() })
		},
	})
}

export const useRenderPrompt = () =>
	useMutation({
		mutationFn: ({ id, values, head }: { id: string; values: Record<string, unknown>; head?: boolean }) =>
			promptsApi.render(id, values, head ?? false),
	})
